import { useCallback, useMemo, useRef, useState } from "react";
import { activatePathway, createPathway, getPathway, updatePathway } from "./adminApi";
// Relative: pure modules, resolvable under Jest without a shim.
import useAdminLoad from "../hooks/useAdminLoad";
import {
  canMoveStep,
  emptyDraft,
  mapStep,
  moveLevel as moveLevelIn,
  moveStep as moveStepIn,
  newLevel,
  removeLevel as removeLevelIn,
  removeStep as removeStepIn,
  reorderLevels as reorderLevelsIn,
  reorderSteps as reorderStepsIn,
  setPathType as setPathTypeIn,
  toDraft,
  toPathwayIn,
  upsertStep as upsertStepIn,
  validateDraft,
} from "./builderModel";

/**
 * The pathway builder (contract §9, spec screen 2): the server's AdminPathway, a local
 * editable draft of it, and the writes that send the draft back.
 *
 * `id === null` is a NEW pathway (/admin/pathways/new): there is nothing to read, the
 * draft starts empty, and the first save is a `POST /pathways` (the screen then moves to
 * the new id's address). Every later save is a FULL `PUT /pathways/{id}` replacement.
 *
 * TWO COPIES, ON PURPOSE. `pathway` is the last AdminPathway the server returned (the
 * baseline for `dirty`, and the source of `activation_problems` and `path_type_locked`);
 * `draft` is what the practitioner is editing. A save replaces both with the server's
 * answer, which is how new levels and steps receive their server ids.
 *
 * `errors` holds the client-side SHAPE problems found by the last save attempt (field key
 * -> "admin" i18n key, pathways/builderModel.validateDraft); each is cleared as its field
 * is edited. `saveError` holds the raw error of the last refused write; the screen turns
 * it into sentences, including a PATHWAY_NOT_ACTIVATABLE's problems.
 *
 * ACTIVATION IS THE SERVER'S (contract §4). `activate()` saves first when there is
 * anything unsaved (so what is activated is what the author sees), then asks the server.
 *
 * @param {string|null} id pathway id, or null for a new pathway
 */
export default function usePathwayBuilder(id) {
  const [pathway, setPathway] = useState(null);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const seq = useRef(0);
  const isNew = !id;

  const load = useCallback(async () => {
    setErrors({});
    setSaveError(null);
    if (!id) {
      setPathway(null);
      setDraft(emptyDraft());
      return;
    }
    const p = await getPathway(id);
    setPathway(p);
    setDraft(toDraft(p));
  }, [id]);
  const { loading, loadError, reload } = useAdminLoad(load);

  /** A client-only row key; never sent (see builderModel.js). */
  const nextKey = useCallback(() => {
    seq.current += 1;
    return `new-${seq.current}`;
  }, []);

  const clearError = useCallback((key) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const edit = useCallback((fn) => {
    setDraft((d) => (d ? fn(d) : d));
  }, []);

  // ── Basics ──
  const setField = useCallback((name, value) => {
    edit((d) => ({ ...d, [name]: value }));
    clearError(name);
    if (name === "start_date" || name === "end_date" || name === "schedule") clearError("dates");
  }, [edit, clearError]);

  /** Switches the path type; going flat merges the levels (the screen confirms first). */
  const setPathType = useCallback((pathType) => {
    edit((d) => setPathTypeIn(d, pathType));
  }, [edit]);

  // ── levels ──
  const addLevel = useCallback(() => {
    const key = nextKey();
    edit((d) => ({ ...d, levels: [...d.levels, newLevel(key)] }));
    return key;
  }, [edit, nextKey]);

  const renameLevel = useCallback((key, title) => {
    edit((d) => ({ ...d, levels: d.levels.map((l) => (l.key === key ? { ...l, title } : l)) }));
    clearError(`level:${key}:title`);
  }, [edit, clearError]);

  const removeLevel = useCallback((key) => edit((d) => removeLevelIn(d, key)), [edit]);
  const moveLevel = useCallback((key, delta) => edit((d) => moveLevelIn(d, key, delta)), [edit]);
  /** Drag-and-drop result: the level keys in their new order. */
  const reorderLevels = useCallback((keys) => edit((d) => reorderLevelsIn(d, keys)), [edit]);

  // ── steps ──
  /** "Save step" of the step editor: inserts a new step into `levelKey`, or replaces an existing one. */
  const upsertStep = useCallback((levelKey, step) => {
    edit((d) => upsertStepIn(d, levelKey, step));
    clearError(`step:${step.key}`);
  }, [edit, clearError]);

  const toggleRequired = useCallback((stepKey) => {
    edit((d) => mapStep(d, stepKey, (s) => ({ ...s, required: !s.required })));
  }, [edit]);

  const removeStep = useCallback((stepKey) => {
    edit((d) => removeStepIn(d, stepKey));
    clearError(`step:${stepKey}`);
  }, [edit, clearError]);

  const moveStep = useCallback((stepKey, delta) => edit((d) => moveStepIn(d, stepKey, delta)), [edit]);
  /** Drag-and-drop result: one level's step keys in their new order. */
  const reorderSteps = useCallback((levelKey, keys) => edit((d) => reorderStepsIn(d, levelKey, keys)), [edit]);
  const canMove = useCallback((stepKey, delta) => Boolean(draft) && canMoveStep(draft, stepKey, delta), [draft]);

  // ── derived ──
  const dirty = useMemo(() => {
    if (!draft) return false;
    const baseline = pathway ? toDraft(pathway) : emptyDraft();
    return JSON.stringify(toPathwayIn(draft)) !== JSON.stringify(toPathwayIn(baseline));
  }, [draft, pathway]);

  // ── writes ──

  /**
   * Validates the draft, then creates it (new) or PUTs it. Resolves the server's
   * AdminPathway on success (both copies replaced by it) and `null` when refused, locally
   * or by the server.
   */
  const save = useCallback(async () => {
    if (!draft) return null;
    const problems = validateDraft(draft);
    setErrors(problems);
    if (Object.keys(problems).length > 0) return null;
    setSaving(true);
    setSaveError(null);
    try {
      const body = toPathwayIn(draft);
      const saved = id ? await updatePathway(id, body) : await createPathway(body);
      setPathway(saved);
      setDraft(toDraft(saved));
      return saved;
    } catch (e) {
      setSaveError(e);
      return null;
    } finally {
      setSaving(false);
    }
  }, [draft, id]);

  /**
   * Saves what is unsaved, then `POST /activate`. Resolves `{ pathway, activated }`:
   * `pathway` is the latest AdminPathway the server returned (the saved one when the
   * activation itself was refused, `null` when even the save was), and `activated` says
   * whether it is now published. A refusal is in `errors` / `saveError`; a 422
   * PATHWAY_NOT_ACTIVATABLE carries its problems. A NEW pathway is created by the save,
   * so the screen reads its id here even when the activation is refused.
   */
  const activate = useCallback(async () => {
    let current = pathway;
    if (!current || dirty) {
      current = await save();
      if (!current) return { pathway: null, activated: false };
    }
    setActivating(true);
    setSaveError(null);
    try {
      const published = await activatePathway(current.id);
      setPathway(published);
      setDraft(toDraft(published));
      return { pathway: published, activated: true };
    } catch (e) {
      setSaveError(e);
      return { pathway: current, activated: false };
    } finally {
      setActivating(false);
    }
  }, [pathway, dirty, save]);

  return {
    isNew, pathway, draft, loading, loadError, reload,
    errors, saving, activating, saveError, dirty, save, activate, nextKey,
    setField, setPathType,
    addLevel, renameLevel, removeLevel, moveLevel, reorderLevels,
    upsertStep, toggleRequired, removeStep, moveStep, reorderSteps, canMove,
  };
}
